import 'office-ui-fabric-react/dist/css/fabric.css';
import { initializeIcons } from '@fluentui/react';
import React, { useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate, useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import useStore, { IStore } from './store';
import * as serviceWorker from '../serviceWorker';
import './App.module.scss';
import './assets/styles/styles.scss';
import { getComponent, isAdmin, isContentCreator, isContentManager, isSuperAdmin, isUser } from './utils/authUtilities';
import { routes } from './routes';
import Footer from './components/footer/Footer';
import Header from './components/header/Header';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from 'authConfig';
import { Loader } from './components/loader/Loader';
import {
    getAccessToken,
    getMSGraphTokenSilently,
    getPowerBITokenSilently,
    getProfileByUserId,
    getProfileImageByUserId
} from './components/helpers';
import { useGetUserDetails } from './services/user-api';
import { UserDto } from 'generated/article-backend';
import { appInsightsService } from './utils/constants';
import { UserRole } from 'generated/request-backend';
import { confirmAppNavigation } from './modules/user/createTicket/creationNavigationConfirmation';

initializeIcons();

const ScrollToTop = (): null => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [pathname]);

    return null;
};

const App: React.FC = () => {
    const setMSGraphAccessToken = useStore((state: IStore) => state.setMSGraphAccessToken);
    const setAccountDetails = useStore((state: IStore) => state.setAccountDetails);
    const setCreationDraftCredential = useStore((state: IStore) => state.setCreationDraftCredential);
    const accessToken = useStore((state: IStore) => state.accessToken);
    const userDetails: UserDto = useStore((state: IStore) => state.userDetails) as UserDto;
    const { isFetched, data: userData } = useGetUserDetails();
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] || {});
    const isAuthenticated = useIsAuthenticated();
    const setUserInfo = useStore((state: IStore) => state.setUserInfo);

    if (inProgress === InteractionStatus.None && !isAuthenticated) {
        instance.loginRedirect(loginRequest);
    }
    useEffect(() => {
        if (userData?.id)
        
            getProfileImageByUserId(userData?.id).then((profile: string) => {
                getProfileByUserId(userData?.id)
                    .then((contactDetail) => {
                        let userInfo = new UserDto({
                            id: userData?.id,
                            displayName: userData?.displayName,
                            email: userData?.email,
                            userRoles: userData?.userRoles,
                            userDomain: userData?.userDomain,
                            contactNumber: contactDetail?.mobilePhone,
                            businessPhones: contactDetail?.businessPhones,
                            profilePic: profile
                        });
                        setUserInfo(userInfo);
                        appInsightsService.setUserDetails(userInfo);
                        appInsightsService.logEvent(
                            "UserProfileFetched: User profile fetched successfully",
                            {
                                userRoles: (userData?.userRoles || []).map((x: UserRole) => x.roleName).join(', '),
                            }
                        );
                    })
                    .catch();
            });
    }, [isFetched, userData]);

    useEffect(() => {
        let cancelled = false;
        if (account)
            getAccessToken('SILENT', instance, loginRequest.scopes, account).then((response) => {
                if (response && !cancelled && response.account?.homeAccountId === account.homeAccountId
                    && instance.getAllAccounts()[0]?.homeAccountId === account.homeAccountId) {
                    localStorage.setItem('accessToken', response.accessToken);
                    setCreationDraftCredential({ accountKey: response.account.homeAccountId, token: response.accessToken });
                    setAccountDetails(response.account);
                    getMSGraphTokenSilently(instance, account).then((accessToken) => {
                        if (accessToken) {
                            setMSGraphAccessToken(accessToken);
                            localStorage.setItem('msGraphAccessToken', accessToken);
                            getPowerBITokenSilently(instance, account, loginRequest.powerBiScope).then((response) => {
                                if (response) localStorage.setItem('powerBIToken', response);
                                return response;
                            });
                        }
                    });
                }
            });
        return () => { cancelled = true; };
    }, [account, instance, setAccountDetails, setCreationDraftCredential, setMSGraphAccessToken]);

    return (
        <>
            <AuthenticatedTemplate>
                {accessToken.length > 0 && (
                        <BrowserRouter getUserConfirmation={confirmAppNavigation}>
                            <ScrollToTop />
                            {userData?.id && userDetails?.id ? (
                                <div className="ms-Grid-col p-a-0">
                                    <Header />

                                    <Switch>
                                        {(isAdmin(userDetails?.userRoles) || isSuperAdmin(userDetails?.userRoles)) && (
                                            <Route path="/" exact component={getComponent(userDetails?.userRoles)} />
                                        )}
                                        {(isContentCreator(userDetails?.userRoles) ||
                                            isContentManager(userDetails?.userRoles)) && (
                                            <Redirect exact from="/" to="/manage-content/articles" />
                                        )}
                                        {isUser(userDetails?.userRoles) && <Redirect exact from="/" to="/knowledge-portal" />}

                                        {routes}
                                    </Switch>

                                    <Footer />
                                </div>
                            ) : (
                                <Loader
                                    classNames="m-t-50 m-b-50 height-70 initial-loader"
                                    loadingText="Loading Surface Knowledge Portal..."
                                />
                            )}
                        </BrowserRouter>
                )}
            </AuthenticatedTemplate>
        </>
    );
};

export default App;

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();